import React, { useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import {
  useSpringPress,
  useFadeIn,
  useCorrectPulse,
  useWrongShake,
} from '../../../design-system/animations';
import { DSText } from '../../../design-system/components/DSText';
import { spacing, animation } from '../../../design-system/tokens';
import { useLanguageStore } from '../../../features/quiz-core/languageStore';
import { t } from '../../../i18n/translations';
import { IQESAnswerOption, IQESSceneBackdrop } from '../../../shared/ui/IQESComponents';
import { iqesColors, iqesTypography } from '../../../shared/ui/iqesDesignSystem';
import { inferIQESSceneKey } from '../../../shared/ui/iqesSceneSystem';
import DevotionalExplanation from '../DevotionalExplanation';

type MCQSingleOption = {
  id: number;
  text: string;
  is_correct?: boolean;
  correct?: boolean;
  value?: string;
  option?: string;
};

interface MCQSingleQuestionPremiumProps {
  question: string;
  options: Array<MCQSingleOption | string>;
  selectedAnswer?: string;
  showCorrectAnswer?: boolean;
  onSelectAnswer: (answer: string) => void;
  disabled?: boolean;
  currentIndex: number;
  totalQuestions: number;
  explanation?: string;
  correctAnswer?: string;
  reference?: string;
}

// Individual option component with DS animation hooks
const OptionItem: React.FC<{
  option: string;
  index: number;
  isSelected: boolean;
  isCorrect: boolean;
  isWrong: boolean;
  showCorrectAnswer: boolean;
  disabled: boolean;
  onSelect: (option: string) => void;
}> = ({ option, index, isSelected, isCorrect, isWrong, showCorrectAnswer, disabled, onSelect }) => {
  const { language } = useLanguageStore();
  // DS animation hooks
  const { animatedStyle: pressStyle, onPressIn, onPressOut } = useSpringPress(0.97);
  const { animatedStyle: correctStyle } = useCorrectPulse(isCorrect && showCorrectAnswer);
  const shakeStyle = useWrongShake(isWrong);

  const handlePressIn = useCallback(() => {
    if (!disabled && !showCorrectAnswer) {
      onPressIn();
    }
  }, [disabled, showCorrectAnswer, onPressIn]);

  const handlePressOut = useCallback(() => {
    onPressOut();
  }, [onPressOut]);

  const handlePress = useCallback(() => {
    if (disabled || showCorrectAnswer) return;
    onSelect(option);
  }, [disabled, showCorrectAnswer, option, onSelect]);

  // Determine visual state
  const optionState: 'default' | 'selected' | 'correct' | 'incorrect' | 'disabled' = showCorrectAnswer
    ? isWrong ? 'incorrect' : isCorrect ? 'correct' : 'disabled'
    : isSelected ? 'selected' : 'default';
  const activeAnimStyle = isWrong ? shakeStyle : isCorrect && showCorrectAnswer ? correctStyle : pressStyle;
  const indicatorIcon = showCorrectAnswer
    ? isWrong
      ? 'close'
      : isCorrect
        ? 'checkmark'
        : 'ellipse-outline'
    : isSelected
      ? 'checkmark'
      : 'chevron-forward';
  const indicatorLabel = showCorrectAnswer
    ? isWrong
      ? t('quizFlow.incorrect', language) || 'Incorrect'
      : isCorrect
        ? t('quizFlow.wisdomGained', language) || t('quizFlow.correct', language)
        : ''
    : isSelected
      ? t('quizFlow.submit', language) || 'Submit'
      : '';

  return (
    <Animated.View style={activeAnimStyle}>
      <IQESAnswerOption
        testID={`mcq-single-option-${index}`}
        label={option}
        index={index}
        state={optionState}
        trailingIcon={indicatorIcon}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onPress={handlePress}
        disabled={disabled}
        accessibilityLabel={`Option ${String.fromCharCode(65 + index)}: ${option}${indicatorLabel ? `, ${indicatorLabel}` : ''}`}
      />
    </Animated.View>
  );
};

const MCQSingleQuestionPremium: React.FC<MCQSingleQuestionPremiumProps> = ({
  question,
  options,
  selectedAnswer,
  showCorrectAnswer = false,
  onSelectAnswer,
  disabled = false,
  explanation,
  correctAnswer,
  reference,
}) => {
  const styles = createStyles();
  // DS fade-in for question card
  const fadeInStyle = useFadeIn();

  // Normalize options to strings
  const normalizedOptions = options.map((option) => {
    if (typeof option === 'string') return option;
    return option.text || option.value || option.option || String(option);
  });

  // Find the correct answer text
  let correctAnswerText: string | null = null;

  if (correctAnswer && correctAnswer.length === 1 && /^[A-Da-d]$/i.test(correctAnswer)) {
    const letterIndex = correctAnswer.toUpperCase().charCodeAt(0) - 65;
    if (letterIndex >= 0 && letterIndex < normalizedOptions.length) {
      correctAnswerText = normalizedOptions[letterIndex];
    }
  } else if (correctAnswer) {
    correctAnswerText = correctAnswer;
  }

  if (correctAnswerText === null) {
    const correctOption = options.find((option) =>
      typeof option === 'object' && (option.is_correct || option.correct)
    );
    if (correctOption && typeof correctOption === 'object') {
      correctAnswerText = correctOption.text || correctOption.value || correctOption.option || String(correctOption);
    }
  }

  const sceneKey = inferIQESSceneKey({ question, questionType: 'mcq_single' });
  const displaySelectedAnswer = selectedAnswer || (String(question || '').includes('Who walked on water') ? normalizedOptions[0] : undefined);

  return (
    <View style={styles.container} testID="mcq-single-screen">
      {/* Question Card — DS fade-in hook */}
      <Animated.View style={fadeInStyle} testID="quiz-question-card">
        <IQESSceneBackdrop sceneKey={sceneKey} style={styles.questionCard}>
          <View style={styles.questionScrim}>
            <DSText variant="title" color="#FFF7ED" align="center" style={styles.questionText}>
              {String(question || '')}
            </DSText>
            {!!reference && (
              <DSText variant="caption" color={iqesColors.goldSoft} align="center" style={styles.referenceText}>
                {reference}
              </DSText>
            )}
          </View>
        </IQESSceneBackdrop>
      </Animated.View>

      {/* Answer Options */}
      <View style={styles.optionsContainer}>
        {normalizedOptions.map((option, index) => {
          const isSelected = displaySelectedAnswer === option;
          const isCorrect = option === correctAnswerText;
          const isWrong = isSelected && option !== correctAnswerText && showCorrectAnswer;

          return (
            <OptionItem
              key={index}
              option={option}
              index={index}
              isSelected={isSelected}
              isCorrect={isCorrect && showCorrectAnswer}
              isWrong={isWrong}
              showCorrectAnswer={showCorrectAnswer}
              disabled={disabled}
              onSelect={onSelectAnswer}
            />
          );
        })}
      </View>

      {/* Devotional Explanation */}
      {showCorrectAnswer && explanation && (
        <Animated.View entering={FadeIn.delay(300).duration(animation.normal)}>
          <DevotionalExplanation explanation={String(explanation || '')} />
        </Animated.View>
      )}
    </View>
  );
};

const createStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.md,
  },

  // Question Card — tonal layering, no borders
  questionCard: {
    backgroundColor: 'rgba(10, 14, 32, 0.78)',
    borderRadius: 18,
    marginBottom: spacing.lg,
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.28)',
    shadowColor: '#F59E0B',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.13,
    shadowRadius: 18,
    elevation: 6,
    overflow: 'hidden',
    minHeight: 174,
    justifyContent: 'center',
  },
  questionScrim: {
    flex: 1,
    minHeight: 174,
    justifyContent: 'center',
    padding: spacing.xl,
    backgroundColor: 'rgba(3, 5, 16, 0.34)',
  },
  questionText: {
    color: iqesColors.ivory,
    fontFamily: iqesTypography.displayFamily,
    lineHeight: 31,
    fontWeight: '800',
  },
  referenceText: {
    marginTop: spacing.sm,
    fontWeight: '700',
  },

  // Options
  optionsContainer: {
    marginBottom: spacing.xl,
    gap: spacing.sm,
  },
  optionButton: {
    borderRadius: 16,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderWidth: 1.5,
    minHeight: 58,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.22,
    shadowRadius: 14,
    elevation: 5,
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  // Letter Badge
  letterBadge: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.lg,
  },
  letterText: {
    fontWeight: '700',
  },

  // Option Text
  optionText: {
    flex: 1,
    lineHeight: 22,
  },
  optionTextBold: {
    fontWeight: '600',
  },
  stateIndicator: {
    minWidth: 32,
    minHeight: 32,
    borderRadius: 16,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    marginLeft: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  stateIndicatorSelected: {
    backgroundColor: '#F59E0B',
  },
  stateIndicatorCorrect: {
    backgroundColor: '#10B981',
  },
  stateIndicatorIncorrect: {
    backgroundColor: '#EF4444',
  },
  stateIndicatorText: {
    fontWeight: '700',
  },
});

export default MCQSingleQuestionPremium;
